import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  BarChart3,
  BookOpen,
  ClipboardCheck,
  ImagePlus,
  Loader2,
  PencilLine,
  Sparkles,
  TimerReset,
  Trash2,
  Upload,
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import { englishService } from '../../services/englishService';
import {
  EnglishAnalyticsMe,
  EnglishCompositionResult,
  EnglishCompositionSubmission,
  EnglishExamReadingResult,
  EnglishExamReadingSession,
  EnglishLiveMode,
  EnglishLiveSessionGrant,
  EnglishState,
} from '../../types/english';
import { EnglishLiveCoachCard } from './EnglishLiveCoachCard';

interface EnglishSkillsPortalProps {
  subjectId: string;
  subjectName: string | null;
  englishState: EnglishState | null;
  onBack: () => void;
  onStateRefresh: () => Promise<void>;
  initialCompositionPrompt?: string | null;
}

const formatMinutes = (seconds: number): string => {
  const safeSeconds = Math.max(seconds || 0, 0);
  return `${Math.floor(safeSeconds / 60)}m ${safeSeconds % 60}s`;
};

const formatPercent = (value: number): string => `${Number.isFinite(value) ? value.toFixed(1) : '0.0'}%`;

const bandLabel = (value?: string | null): string => {
  const raw = String(value || '').trim();
  if (!raw) return 'Developing';
  return raw.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const compositionRubricOrder = [
  'content_relevance',
  'organization',
  'grammar_sentence_control',
  'spelling',
  'punctuation',
];

export const EnglishSkillsPortal: React.FC<EnglishSkillsPortalProps> = ({
  subjectId,
  subjectName,
  englishState,
  onBack,
  onStateRefresh,
  initialCompositionPrompt,
}) => {
  const [examSession, setExamSession] = useState<EnglishExamReadingSession | null>(null);
  const [examAnswers, setExamAnswers] = useState<Record<string, string>>({});
  const [examResult, setExamResult] = useState<EnglishExamReadingResult | null>(null);
  const [examStartedAtMs, setExamStartedAtMs] = useState<number | null>(null);
  const [analytics, setAnalytics] = useState<EnglishAnalyticsMe | null>(null);
  const [liveGrants, setLiveGrants] = useState<Partial<Record<EnglishLiveMode, EnglishLiveSessionGrant>>>({});
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [isStartingExam, setIsStartingExam] = useState(false);
  const [isSubmittingExam, setIsSubmittingExam] = useState(false);
  const [issuingMode, setIssuingMode] = useState<EnglishLiveMode | null>(null);
  const [compositionPromptTopic, setCompositionPromptTopic] = useState('');
  const [compositionText, setCompositionText] = useState('');
  const [compositionFiles, setCompositionFiles] = useState<File[]>([]);
  const [compositionDraft, setCompositionDraft] = useState<EnglishCompositionSubmission | null>(null);
  const [compositionResult, setCompositionResult] = useState<EnglishCompositionResult | null>(null);
  const [compositionConfirmedText, setCompositionConfirmedText] = useState('');
  const [isCreatingCompositionDraft, setIsCreatingCompositionDraft] = useState(false);
  const [isMarkingComposition, setIsMarkingComposition] = useState(false);

  const refreshAnalytics = useCallback(async () => {
    if (!subjectId) return;
    setIsLoadingAnalytics(true);
    try {
      const nextAnalytics = await englishService.getMyAnalytics(subjectId);
      setAnalytics(nextAnalytics);
    } catch (error) {
      console.error('Failed to load Reading & Composition analytics:', error);
      setAnalytics(null);
    } finally {
      setIsLoadingAnalytics(false);
    }
  }, [subjectId]);

  useEffect(() => {
    setExamSession(null);
    setExamAnswers({});
    setExamResult(null);
    setExamStartedAtMs(null);
    setLiveGrants({});
    setCompositionPromptTopic('');
    setCompositionText('');
    setCompositionFiles([]);
    setCompositionDraft(null);
    setCompositionResult(null);
    setCompositionConfirmedText('');
  }, [subjectId]);

  useEffect(() => {
    if (initialCompositionPrompt?.trim()) {
      setCompositionPromptTopic(initialCompositionPrompt.trim());
    }
  }, [initialCompositionPrompt]);

  useEffect(() => {
    void refreshAnalytics();
  }, [refreshAnalytics]);

  const readingUsageSummary = useMemo(() => {
    if (!analytics) {
      return {
        attempts: 0,
        accuracy: 0,
        wpm: 0,
        compositionAttempts: 0,
      };
    }
    return {
      attempts: analytics.exam_attempts_count,
      accuracy: analytics.average_comprehension_accuracy,
      wpm: analytics.average_words_per_minute,
      compositionAttempts: analytics.composition_attempts_count,
    };
  }, [analytics]);

  const liveUsage = englishState?.live_usage;
  const showOralCard = Boolean(englishState?.english_oral_reading_enabled);
  const showSpeakingCard = Boolean(englishState?.english_speaking_enabled);
  const showAnyLiveCard = showOralCard || showSpeakingCard;

  const handleStartExam = async () => {
    setIsStartingExam(true);
    try {
      const nextSession = await englishService.createExamReadingSession(subjectId);
      setExamSession(nextSession);
      setExamAnswers({});
      setExamResult(null);
      setExamStartedAtMs(Date.now());
      await onStateRefresh();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Could not start a Reading Core session.');
    } finally {
      setIsStartingExam(false);
    }
  };

  const handleSubmitExam = async () => {
    if (!examSession) return;
    setIsSubmittingExam(true);
    try {
      const readingTimeSeconds = Math.max(
        1,
        Math.round(((Date.now() - (examStartedAtMs || Date.now())) / 1000)),
      );
      const result = await englishService.submitExamReadingSession(
        examSession.session_id,
        readingTimeSeconds,
        examSession.questions.map((question) => ({
          question_id: question.question_id,
          answer: examAnswers[question.question_id] || '',
        })),
      );
      setExamResult(result);
      setExamSession(null);
      setExamStartedAtMs(null);
      await Promise.all([onStateRefresh(), refreshAnalytics()]);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Could not score this reading session.');
    } finally {
      setIsSubmittingExam(false);
    }
  };

  const handleIssueLive = async (mode: EnglishLiveMode) => {
    setIssuingMode(mode);
    try {
      const grant = await englishService.issueLiveSessionToken(subjectId, mode);
      setLiveGrants((current) => ({ ...current, [mode]: grant }));
      await Promise.all([onStateRefresh(), refreshAnalytics()]);
      toast.success(`${mode === 'oral_reading' ? 'Oral Reading' : 'Speaking'} session reserved.`);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Could not prepare a live coaching session.');
    } finally {
      setIssuingMode(null);
    }
  };

  const handleCompositionFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const nextFiles = Array.from(event.target.files || []);
    if (nextFiles.length > 3) {
      toast.error('Upload at most 3 clear composition images at a time.');
      event.target.value = '';
      return;
    }
    const invalidFile = nextFiles.find((file) => !file.type.startsWith('image/'));
    if (invalidFile) {
      toast.error('Composition uploads must be image files.');
      event.target.value = '';
      return;
    }
    setCompositionFiles(nextFiles);
    event.target.value = '';
  };

  const handleRemoveCompositionFile = (index: number) => {
    setCompositionFiles((current) => current.filter((_, currentIndex) => currentIndex !== index));
  };

  const handleCreateCompositionDraft = async () => {
    if (!compositionFiles.length && !compositionText.trim()) {
      toast.error('Upload composition photos or paste the composition text first.');
      return;
    }
    setIsCreatingCompositionDraft(true);
    try {
      const draft = await englishService.createCompositionSubmission(subjectId, {
        promptTopic: compositionPromptTopic,
        submissionText: compositionText,
        files: compositionFiles,
      });
      setCompositionDraft(draft);
      setCompositionConfirmedText(draft.confirmed_text || draft.ocr_text || compositionText);
      setCompositionResult(null);
      toast.success(compositionFiles.length ? 'OCR preview is ready. Check it before marking.' : 'Composition draft is ready for marking.');
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Could not prepare this composition for marking.');
    } finally {
      setIsCreatingCompositionDraft(false);
    }
  };

  const handleMarkComposition = async () => {
    if (!compositionDraft?.submission_id) {
      toast.error('Create an OCR preview or typed draft first.');
      return;
    }
    if (!compositionConfirmedText.trim()) {
      toast.error('Please confirm the composition text before marking.');
      return;
    }
    setIsMarkingComposition(true);
    try {
      const result = await englishService.markCompositionSubmission(
        compositionDraft.submission_id,
        compositionConfirmedText,
      );
      setCompositionResult(result);
      await Promise.all([onStateRefresh(), refreshAnalytics()]);
      toast.success('Composition marked successfully.');
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Could not mark this composition.');
    } finally {
      setIsMarkingComposition(false);
    }
  };

  const handleResetComposition = () => {
    setCompositionDraft(null);
    setCompositionResult(null);
    setCompositionConfirmedText('');
    setCompositionText('');
    setCompositionFiles([]);
  };

  const renderQuestion = (question: EnglishExamReadingSession['questions'][number]) => {
    const currentValue = examAnswers[question.question_id] || '';
    if (question.type === 'multiple_choice') {
      return (
        <div className="grid gap-2">
          {question.options.map((option) => {
            const selected = currentValue === option;
            return (
              <button
                key={option}
                type="button"
                onClick={() =>
                  setExamAnswers((current) => ({
                    ...current,
                    [question.question_id]: option,
                  }))
                }
                className={`rounded-xl border px-3 py-2 text-left text-sm transition-colors ${
                  selected
                    ? 'border-blue-500/50 bg-blue-500/10 text-[var(--text-primary)]'
                    : 'border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:bg-[var(--hover-bg)]'
                }`}
              >
                {option}
              </button>
            );
          })}
        </div>
      );
    }
    return (
      <textarea
        value={currentValue}
        onChange={(event) =>
          setExamAnswers((current) => ({
            ...current,
            [question.question_id]: event.target.value,
          }))
        }
        rows={3}
        className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:ring-2 focus:ring-blue-500/40"
        placeholder="Type your answer here..."
      />
    );
  };

  const renderReadingResult = () => {
    if (!examResult) return null;
    return (
      <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-[var(--text-primary)]">Latest Reading Result</h3>
            <p className="text-sm text-[var(--text-secondary)]">
              {examResult.correct_answers} / {examResult.total_questions} correct
            </p>
          </div>
          <div className="inline-flex items-center rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-sm font-semibold text-emerald-300">
            {formatPercent(examResult.comprehension_accuracy_pct)}
          </div>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4">
            <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Pacing</p>
            <p className="mt-2 text-sm font-medium capitalize text-[var(--text-primary)]">{examResult.pacing_band.replace('_', ' ')}</p>
            <p className="mt-2 text-xs text-[var(--text-secondary)]">{examResult.pacing_feedback}</p>
          </div>
          <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4">
            <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Reading time</p>
            <p className="mt-2 text-sm font-medium text-[var(--text-primary)]">
              {formatMinutes(examResult.reading_time_seconds)} / expected {formatMinutes(examResult.expected_duration_seconds)}
            </p>
            <p className="mt-2 text-xs text-[var(--text-secondary)]">{examResult.words_per_minute.toFixed(1)} words/minute</p>
          </div>
          <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4">
            <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Coaching</p>
            <p className="mt-2 text-sm text-[var(--text-primary)]">{examResult.coaching_summary}</p>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {examResult.question_results.map((result, index) => (
            <div
              key={result.question_id}
              className={`rounded-xl border px-4 py-3 ${
                result.correct
                  ? 'border-emerald-500/20 bg-emerald-500/5'
                  : 'border-amber-500/20 bg-amber-500/5'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-[var(--text-primary)]">Question {index + 1}</p>
                  <p className="mt-1 text-xs uppercase tracking-wide text-[var(--text-tertiary)]">{result.skill}</p>
                </div>
                <span className={`text-xs font-semibold ${result.correct ? 'text-emerald-300' : 'text-amber-300'}`}>
                  {result.correct ? 'Correct' : 'Review'}
                </span>
              </div>
              {!result.correct && (
                <>
                  <p className="mt-3 text-sm text-[var(--text-secondary)]">
                    Your answer: <span className="text-[var(--text-primary)]">{result.learner_answer || 'No answer'}</span>
                  </p>
                  <p className="mt-1 text-sm text-[var(--text-secondary)]">
                    Expected: <span className="text-[var(--text-primary)]">{result.expected_answer || 'See explanation'}</span>
                  </p>
                </>
              )}
              {result.explanation && (
                <p className="mt-2 text-sm text-[var(--text-secondary)]">{result.explanation}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCompositionResult = () => {
    if (!compositionResult) return null;
    return (
      <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="text-lg font-semibold text-[var(--text-primary)]">Latest Composition Feedback</h4>
            <p className="text-sm text-[var(--text-secondary)]">
              {compositionResult.prompt_topic || 'Composition submission'}
            </p>
          </div>
          <span className="inline-flex items-center rounded-full border border-amber-500/20 bg-amber-500/10 px-3 py-1 text-sm font-semibold text-amber-200">
            {bandLabel(compositionResult.overall_band)}
          </span>
        </div>

        <p className="mt-4 text-sm text-[var(--text-primary)]">{compositionResult.feedback_summary}</p>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-4">
            <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Strengths</p>
            <ul className="mt-3 space-y-2 text-sm text-[var(--text-primary)]">
              {compositionResult.strengths.map((item) => (
                <li key={item} className="flex gap-2">
                  <span className="text-emerald-300">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-4">
            <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Fix next</p>
            <ul className="mt-3 space-y-2 text-sm text-[var(--text-primary)]">
              {compositionResult.fix_next.map((item) => (
                <li key={item} className="flex gap-2">
                  <span className="text-amber-300">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          {compositionRubricOrder.map((key) => {
            const value = compositionResult.rubric_scores[key];
            if (!value) return null;
            return (
              <div key={key} className="rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-3">
                <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">{key.replace(/_/g, ' ')}</p>
                <p className="mt-2 text-sm font-medium text-[var(--text-primary)]">{bandLabel(value.band)}</p>
                <p className="mt-2 text-xs text-[var(--text-secondary)]">{value.note}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-4 rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-4">
          <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Rewrite task</p>
          <p className="mt-2 text-sm text-[var(--text-primary)]">{compositionResult.rewrite_task}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-6xl p-6 sm:p-8">
      <div className="mb-8 flex items-start gap-4">
        <button
          onClick={onBack}
          className="mt-1 rounded-lg p-2 text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex flex-1 items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-amber-500/20 bg-gradient-to-br from-amber-500/20 to-orange-500/5">
            <Sparkles className="h-5 w-5 text-amber-300" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-[var(--text-primary)]">Reading &amp; Composition</h2>
            <p className="text-sm text-[var(--text-secondary)]">
              Reading practice, composition marking, and optional live English coaching in one place.
            </p>
          </div>
        </div>
      </div>

      {subjectName && (
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/20 bg-amber-500/10 px-3 py-1.5 text-xs font-medium text-amber-300">
            <BookOpen className="h-3 w-3" />
            {subjectName}
          </span>
          {englishState && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-color)] bg-[var(--surface-elevated)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)]">
              Reading band: <span className="capitalize text-[var(--text-primary)]">{englishState.reading_skill_band}</span>
            </span>
          )}
        </div>
      )}

      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-4">
          <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Reading attempts</p>
          <p className="mt-2 text-2xl font-semibold text-[var(--text-primary)]">{readingUsageSummary.attempts}</p>
        </div>
        <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-4">
          <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Avg comprehension</p>
          <p className="mt-2 text-2xl font-semibold text-[var(--text-primary)]">{formatPercent(readingUsageSummary.accuracy)}</p>
        </div>
        <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-4">
          <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Avg reading pace</p>
          <p className="mt-2 text-2xl font-semibold text-[var(--text-primary)]">{readingUsageSummary.wpm.toFixed(1)}</p>
          <p className="mt-1 text-xs text-[var(--text-secondary)]">words per minute</p>
        </div>
        <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-4">
          <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Composition attempts</p>
          <p className="mt-2 text-2xl font-semibold text-[var(--text-primary)]">{readingUsageSummary.compositionAttempts}</p>
        </div>
        <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-4">
          <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Live minutes</p>
          <p className="mt-2 text-2xl font-semibold text-[var(--text-primary)]">
            {formatMinutes(liveUsage?.total_used_seconds || 0)}
          </p>
          <p className="mt-1 text-xs text-[var(--text-secondary)]">of {formatMinutes(liveUsage?.total_cap_seconds || 0)}</p>
        </div>
      </div>

      {englishState?.live_notes?.length ? (
        <div className="mb-6 rounded-2xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-4">
          <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Reading &amp; Composition notes</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {englishState.live_notes.map((note) => (
              <span
                key={note}
                className="inline-flex items-center rounded-full border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-1 text-xs font-medium text-[var(--text-secondary)]"
              >
                {note}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <div className="space-y-6">
        <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-[var(--text-primary)]">Reading Core</h3>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">
                Timed passage reading with comprehension questions, pacing classification, and next-step coaching.
              </p>
            </div>
            <button
              type="button"
              onClick={() => void handleStartExam()}
              disabled={!englishState?.english_exam_reading_enabled || isStartingExam || Boolean(examSession)}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-60"
            >
              {isStartingExam ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />}
              {examSession ? 'Session in Progress' : 'Start Session'}
            </button>
          </div>

          {!englishState?.english_exam_reading_enabled ? (
            <p className="mt-4 text-sm text-[var(--text-secondary)]">Reading Core is not enabled for this subject yet.</p>
          ) : examSession ? (
            <div className="mt-5 space-y-5">
              <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-5">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center rounded-full border border-blue-500/20 bg-blue-500/10 px-3 py-1 text-xs font-semibold text-blue-300">
                    {examSession.title}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-[var(--border-color)] bg-[var(--surface-elevated)] px-3 py-1 text-xs font-medium text-[var(--text-secondary)]">
                    {examSession.word_count} words
                  </span>
                  <span className="inline-flex items-center rounded-full border border-[var(--border-color)] bg-[var(--surface-elevated)] px-3 py-1 text-xs font-medium text-[var(--text-secondary)]">
                    Expected time {formatMinutes(examSession.expected_duration_seconds)}
                  </span>
                </div>
                <p className="mt-4 whitespace-pre-line text-[15px] leading-7 text-[var(--text-primary)]">
                  {examSession.passage_text}
                </p>
              </div>

              <div className="space-y-4">
                {examSession.questions.map((question, index) => (
                  <div key={question.question_id} className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-5">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center rounded-full border border-[var(--border-color)] bg-[var(--surface-elevated)] px-2.5 py-1 text-xs font-semibold text-[var(--text-secondary)]">
                        Question {index + 1}
                      </span>
                      <span className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">{question.skill}</span>
                    </div>
                    <p className="mt-3 text-sm font-medium text-[var(--text-primary)]">{question.prompt}</p>
                    <div className="mt-4">{renderQuestion(question)}</div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between gap-3 rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4">
                <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                  <TimerReset className="h-4 w-4" />
                  Your timer runs from when you start reading until you submit the answers.
                </div>
                <button
                  type="button"
                  onClick={() => void handleSubmitExam()}
                  disabled={isSubmittingExam}
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-60"
                >
                  {isSubmittingExam ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />}
                  Submit Answers
                </button>
              </div>
            </div>
          ) : (
            <p className="mt-4 text-sm text-[var(--text-secondary)]">
              Start a session to open a passage, track timing, and answer comprehension questions.
            </p>
          )}
        </div>

        {renderReadingResult()}

        <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-[var(--text-primary)]">Composition</h3>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">
                Take clear photos of your written composition and upload them for marking. You can also paste or type the text if needed.
              </p>
            </div>
            <button
              type="button"
              onClick={handleResetComposition}
              className="inline-flex items-center gap-2 rounded-xl border border-[var(--border-color)] px-3 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--hover-bg)]"
            >
              <PencilLine className="h-4 w-4" />
              New Draft
            </button>
          </div>

          <div className="mt-4 space-y-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Choose a prompt or type your own topic</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {(englishState?.suggested_composition_prompts || []).map((prompt) => {
                  const selected = compositionPromptTopic.trim() === prompt.trim();
                  return (
                    <button
                      key={prompt}
                      type="button"
                      onClick={() => setCompositionPromptTopic(prompt)}
                      className={`rounded-full border px-3 py-2 text-left text-xs transition-colors ${
                        selected
                          ? 'border-amber-500/35 bg-amber-500/10 text-amber-100'
                          : 'border-amber-400/25 bg-[var(--bg-secondary)] text-[var(--text-primary)] hover:bg-[var(--hover-bg)]'
                      }`}
                    >
                      {prompt}
                    </button>
                  );
                })}
              </div>
              <input
                value={compositionPromptTopic}
                onChange={(event) => setCompositionPromptTopic(event.target.value)}
                className="mt-3 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                placeholder="Topic or prompt (optional if you already wrote one)"
              />
            </div>

            <div className="grid gap-4 lg:grid-cols-[1.05fr_0.95fr]">
              <div className="rounded-2xl border border-dashed border-amber-500/30 bg-[var(--bg-secondary)] p-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 p-2">
                    <ImagePlus className="h-5 w-5 text-amber-300" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[var(--text-primary)]">Upload 1 to 3 photos of written work</p>
                    <p className="mt-1 text-xs text-[var(--text-secondary)]">
                      Use bright light, keep the page flat, and make sure each line is readable.
                    </p>
                  </div>
                </div>
                <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600">
                  <Upload className="h-4 w-4" />
                  Choose Images
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleCompositionFilesSelected}
                  />
                </label>
                {compositionFiles.length ? (
                  <div className="mt-4 space-y-2">
                    {compositionFiles.map((file, index) => (
                      <div
                        key={`${file.name}-${index}`}
                        className="flex items-center justify-between gap-3 rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] px-3 py-2 text-sm"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-[var(--text-primary)]">{file.name}</p>
                          <p className="text-xs text-[var(--text-secondary)]">{(file.size / (1024 * 1024)).toFixed(1)} MB</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveCompositionFile(index)}
                          className="rounded-lg p-1 text-[var(--text-secondary)] hover:bg-[var(--hover-bg)] hover:text-[var(--text-primary)]"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-4 text-xs text-[var(--text-secondary)]">No images selected yet.</p>
                )}
              </div>

              <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4">
                <p className="text-sm font-medium text-[var(--text-primary)]">Paste or type the composition if needed</p>
                <p className="mt-1 text-xs text-[var(--text-secondary)]">
                  This is useful if OCR misses some handwriting, or if the learner typed the work already.
                </p>
                <textarea
                  value={compositionText}
                  onChange={(event) => setCompositionText(event.target.value)}
                  rows={10}
                  className="mt-4 w-full rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                  placeholder="Paste or type the composition here if you are not uploading photos, or use it as a fallback if OCR misses some lines."
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => void handleCreateCompositionDraft()}
                disabled={isCreatingCompositionDraft}
                className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600 disabled:opacity-60"
              >
                {isCreatingCompositionDraft ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                {compositionFiles.length ? 'Create OCR Preview' : 'Prepare Composition Draft'}
              </button>
              <p className="text-xs text-[var(--text-secondary)]">
                Photos are the preferred path for marking actual written work. Typed text is the fallback.
              </p>
            </div>

            {compositionDraft ? (
              <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h4 className="text-lg font-semibold text-[var(--text-primary)]">OCR Preview &amp; Confirmation</h4>
                    <p className="mt-1 text-sm text-[var(--text-secondary)]">
                      Edit any OCR mistakes before you ask for the final composition feedback.
                    </p>
                  </div>
                  <span className="inline-flex items-center rounded-full border border-[var(--border-color)] bg-[var(--surface-elevated)] px-3 py-1 text-xs font-medium text-[var(--text-secondary)]">
                    Source: {compositionDraft.source_mode}
                  </span>
                </div>
                <textarea
                  value={compositionConfirmedText}
                  onChange={(event) => setCompositionConfirmedText(event.target.value)}
                  rows={12}
                  className="mt-4 w-full rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] px-3 py-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                />
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => void handleMarkComposition()}
                    disabled={isMarkingComposition}
                    className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-60"
                  >
                    {isMarkingComposition ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />}
                    Mark Composition
                  </button>
                  <p className="text-xs text-[var(--text-secondary)]">
                    Confirm the text carefully so the feedback reflects what the learner actually wrote.
                  </p>
                </div>
              </div>
            ) : null}

            {renderCompositionResult()}
          </div>
        </div>

        {showAnyLiveCard ? (
          <div className="grid gap-6 lg:grid-cols-2">
            {showOralCard ? (
              <EnglishLiveCoachCard
                mode="oral_reading"
                englishState={englishState}
                grant={liveGrants.oral_reading || null}
                isIssuing={issuingMode === 'oral_reading'}
                onReserve={handleIssueLive}
                onCompleted={async () => {
                  await Promise.all([onStateRefresh(), refreshAnalytics()]);
                }}
              />
            ) : null}
            {showSpeakingCard ? (
              <EnglishLiveCoachCard
                mode="speaking"
                englishState={englishState}
                grant={liveGrants.speaking || null}
                isIssuing={issuingMode === 'speaking'}
                onReserve={handleIssueLive}
                onCompleted={async () => {
                  await Promise.all([onStateRefresh(), refreshAnalytics()]);
                }}
              />
            ) : null}
          </div>
        ) : null}

        <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-[var(--text-primary)]">Learner Analytics</h3>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">
                Track reading, composition, pacing, and English live-practice activity over time.
              </p>
            </div>
            {isLoadingAnalytics ? <Loader2 className="h-5 w-5 animate-spin text-[var(--text-secondary)]" /> : <BarChart3 className="h-5 w-5 text-[var(--text-secondary)]" />}
          </div>

          {analytics ? (
            <div className="mt-4 grid gap-4 xl:grid-cols-3">
              <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4">
                <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Recent reading sessions</p>
                <div className="mt-3 space-y-3">
                  {analytics.recent_exam_sessions.length > 0 ? (
                    analytics.recent_exam_sessions.map((item) => (
                      <div key={item.session_id} className="rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-3">
                        <p className="text-sm font-medium text-[var(--text-primary)]">{item.title}</p>
                        <p className="mt-1 text-xs text-[var(--text-secondary)]">
                          {formatPercent(item.comprehension_accuracy_pct)} · {item.pacing_band.replace('_', ' ')} · {formatMinutes(item.reading_time_seconds)}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-[var(--text-secondary)]">No completed reading sessions yet.</p>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4">
                <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Recent compositions</p>
                <div className="mt-3 space-y-3">
                  {analytics.recent_composition_submissions.length > 0 ? (
                    analytics.recent_composition_submissions.map((item) => (
                      <div key={item.submission_id} className="rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-3">
                        <p className="text-sm font-medium text-[var(--text-primary)]">{item.prompt_topic || 'Composition submission'}</p>
                        <p className="mt-1 text-xs text-[var(--text-secondary)]">
                          {bandLabel(item.overall_band)} · {new Date(item.marked_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-[var(--text-secondary)]">No marked compositions yet.</p>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4">
                <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Usage summary</p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-3">
                    <p className="text-xs text-[var(--text-secondary)]">Latest pacing</p>
                    <p className="mt-1 text-sm font-medium capitalize text-[var(--text-primary)]">
                      {analytics.latest_pacing_band ? analytics.latest_pacing_band.replace('_', ' ') : 'Not enough data yet'}
                    </p>
                  </div>
                  <div className="rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-3">
                    <p className="text-xs text-[var(--text-secondary)]">Latest composition</p>
                    <p className="mt-1 text-sm font-medium text-[var(--text-primary)]">
                      {analytics.latest_composition_submitted_at
                        ? new Date(analytics.latest_composition_submitted_at).toLocaleDateString()
                        : 'Not marked yet'}
                    </p>
                  </div>
                  <div className="rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-3">
                    <p className="text-xs text-[var(--text-secondary)]">Live practice</p>
                    <p className="mt-1 text-sm font-medium text-[var(--text-primary)]">
                      Oral {analytics.oral_attempts_count} · Speaking {analytics.speaking_attempts_count}
                    </p>
                  </div>
                  <div className="rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-3">
                    <p className="text-xs text-[var(--text-secondary)]">Composition attempts</p>
                    <p className="mt-1 text-sm font-medium text-[var(--text-primary)]">{analytics.composition_attempts_count}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p className="mt-4 text-sm text-[var(--text-secondary)]">Analytics will appear after your first reading, composition, or live English activity.</p>
          )}
        </div>
      </div>
    </div>
  );
};
